import type { LatLng, Route, RouteStep } from "./types";
import { distanceMeters as sharedDistanceMeters } from "../../../build/js/packages/derech-shared/kotlin/derech-shared.mjs";

const EARTH_RADIUS = 6_371_008.8;
const radians = (degrees: number) => (degrees * Math.PI) / 180;
const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));
const longitudeDelta = (a: number, b: number) => ((b - a + 540) % 360) - 180;

export function distanceMeters(a: LatLng, b: LatLng): number {
  return sharedDistanceMeters(a.lat, a.lng, b.lat, b.lng);
}

export interface NavigationProgress {
  remainingDistance: number;
  remainingDuration: number;
  /** Index of the next maneuver; a departure step is skipped when a subsequent step exists. */
  stepIndex: number;
  distanceToStep: number;
  /** Distance to the matched geometry, independent of the navigation off-route threshold. */
  distanceFromRoute: number;
  offRoute: boolean;
  arrived: boolean;
  /** Fraction of the route covered, from 0 to 1. */
  progress: number;
}

interface Projection {
  distance: number;
  along: number;
}

/** Project onto a short road segment in a local tangent plane, including across the date line. */
function project(
  position: LatLng,
  a: LatLng,
  b: LatLng,
  start: number,
  length: number,
  low = 0,
  high = 1,
): Projection {
  const scale =
    EARTH_RADIUS * Math.cos(radians((a.lat + b.lat + position.lat) / 3));
  const bx = radians(longitudeDelta(a.lng, b.lng)) * scale;
  const by = radians(b.lat - a.lat) * EARTH_RADIUS;
  const px = radians(longitudeDelta(a.lng, position.lng)) * scale;
  const py = radians(position.lat - a.lat) * EARTH_RADIUS;
  const denominator = bx * bx + by * by;
  const fraction =
    denominator > 0 ? clamp((px * bx + py * by) / denominator, low, high) : 0;
  return {
    distance: Math.hypot(px - bx * fraction, py - by * fraction),
    along: start + length * fraction,
  };
}

/**
 * Progress is matched to road geometry, with a bounded continuity window once navigation starts.
 * This prevents a nearby return lane, hairpin, or overpass from skipping kilometers of a route.
 * After a prolonged GPS gap, callers should obtain a fresh route rather than reuse stale progress.
 */
export function computeProgress(
  route: Route,
  position: LatLng,
  previousProgress?: number,
): NavigationProgress {
  const points = route.geometry
    .filter(
      (point) =>
        point.length >= 2 &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1]),
    )
    .map((point) => ({ lat: point[1], lng: point[0] }));
  const lengths = points
    .slice(1)
    .map((point, i) => distanceMeters(points[i], point));
  const total = lengths.reduce((sum, length) => sum + length, 0);
  if (points.length < 2 || total < 0.01) {
    const arrived =
      !!points.length &&
      route.distance < 40 &&
      distanceMeters(position, points[points.length - 1]) < 20;
    return {
      remainingDistance: arrived ? 0 : Math.max(0, route.distance),
      remainingDuration: arrived ? 0 : Math.max(0, route.duration),
      stepIndex: Math.max(0, route.steps.length - 1),
      distanceToStep: Math.max(0, route.distance),
      distanceFromRoute: points.length
        ? distanceMeters(position, points[0])
        : Infinity,
      offRoute: !arrived,
      arrived,
      progress: arrived ? 1 : 0,
    };
  }
  const previous =
    previousProgress !== undefined && Number.isFinite(previousProgress)
      ? clamp(previousProgress, 0, 1) * total
      : undefined;
  const lower = previous === undefined ? 0 : Math.max(0, previous - 100);
  const upper =
    previous === undefined ? total : Math.min(total, previous + 450);
  let chosen: Projection | undefined;
  let bestScore = Infinity;
  let accumulated = 0;
  for (let i = 0; i < lengths.length; i++) {
    const length = lengths[i];
    if (length > 0 && accumulated <= upper && accumulated + length >= lower) {
      const candidate = project(
        position,
        points[i],
        points[i + 1],
        accumulated,
        length,
        clamp((lower - accumulated) / length, 0, 1),
        clamp((upper - accumulated) / length, 0, 1),
      );
      const change = previous === undefined ? 0 : candidate.along - previous;
      const score =
        candidate.distance +
        Math.max(0, change - 30) * 0.06 +
        Math.max(0, -change) * 0.08;
      // At an initial crossing/loop, prefer its earliest segment when both are within GPS accuracy.
      const initialTie =
        previous === undefined &&
        chosen !== undefined &&
        Math.abs(candidate.distance - chosen.distance) < 8;
      if (score < bestScore && !initialTie) {
        chosen = candidate;
        bestScore = score;
      }
    }
    accumulated += length;
  }
  const offRoute = !chosen || chosen.distance > 60;
  let along = chosen?.along ?? previous ?? 0;
  if (previous !== undefined)
    along = offRoute ? previous : Math.max(previous, along);
  const progress = clamp(along / total, 0, 1);
  const traveled = progress * route.distance;
  const destinationDistance = distanceMeters(
    position,
    points[points.length - 1],
  );
  const arrived =
    !offRoute &&
    destinationDistance <= 35 &&
    progress >= 0.98 &&
    total - along <= 40;
  let stepIndex = 0;
  let anchor = 0;
  while (
    stepIndex < route.steps.length - 1 &&
    (traveled > anchor + 12 ||
      (stepIndex === 0 && route.steps[0].type === "depart"))
  ) {
    anchor += Math.max(0, route.steps[stepIndex].distance);
    stepIndex++;
  }
  if (arrived) stepIndex = Math.max(0, route.steps.length - 1);
  let remainingDuration = Math.max(0, route.duration * (1 - progress));
  const durations = route.segmentDurations;
  const annotationTotal =
    durations?.reduce((sum, seconds) => sum + seconds, 0) ?? 0;
  if (
    durations &&
    points.length === route.geometry.length &&
    durations.length === lengths.length &&
    durations.every((seconds) => Number.isFinite(seconds) && seconds >= 0) &&
    Number.isFinite(annotationTotal) &&
    annotationTotal > 0
  ) {
    // Retain each road segment's speed profile while including the provider's
    // route-level timing adjustments, just as the server's traffic baseline does.
    let segmentStart = 0;
    let remainingAnnotations = 0;
    lengths.forEach((length, index) => {
      if (length > 0)
        remainingAnnotations +=
          durations[index] *
          clamp((segmentStart + length - along) / length, 0, 1);
      segmentStart += length;
    });
    remainingDuration = Math.max(
      0,
      (remainingAnnotations / annotationTotal) * route.duration,
    );
  }
  return {
    remainingDistance: arrived ? 0 : Math.max(0, route.distance - traveled),
    remainingDuration: arrived ? 0 : remainingDuration,
    stepIndex,
    distanceToStep: arrived
      ? 0
      : route.steps.length
        ? Math.max(0, anchor - traveled)
        : Math.max(0, route.distance - traveled),
    distanceFromRoute: chosen?.distance ?? Infinity,
    offRoute,
    arrived,
    progress: arrived ? 1 : progress,
  };
}

export function formatDistance(meters: number): string {
  const safe = Math.max(0, Number.isFinite(meters) ? meters : 0);
  if (safe < 1_000) return `${Math.round(safe / 10) * 10} מ׳`;
  return `${(safe / 1_000).toLocaleString("he-IL", { maximumFractionDigits: safe < 10_000 ? 1 : 0 })} ק״מ`;
}

/** An upcoming arrival maneuver must not announce that the destination was reached. */
export function guidanceInstruction(
  step: RouteStep | undefined,
  arrived = false,
): string {
  if (arrived) return "הגעת ליעד. דרך טובה!";
  if (step?.type === "arrive") return "ממשיכים לעבר היעד";
  return step?.instruction || "ממשיכים בדרך";
}

export function formatDuration(seconds: number): string {
  const minutes = Math.max(
    1,
    Math.ceil((Number.isFinite(seconds) ? Math.max(0, seconds) : 0) / 60),
  );
  if (minutes < 60) return `${minutes} דק׳`;
  const remainder = minutes % 60;
  return `${Math.floor(minutes / 60)} שע׳${remainder ? ` ${remainder} דק׳` : ""}`;
}
